import { AppDispatch } from 'store';
import { authActions } from 'store/slices/auth';
import { signOut as cognitoSignOut } from 'aws-amplify/auth';
import cookies, { COOKIE_OPTIONS } from 'config/cookie';
import { JWT_ACCESS_TOKEN } from 'constants/jwt-tokens';
import { QueryClient } from '@tanstack/react-query';
import { clearSentryUser, captureSentryException } from 'config/sentry';
import AuthService from 'services/AuthService';

/**
 * @description Sign out the user, remove authentication tokens from cookies, clear Redux state,
 * invalidate cached queries, and clear Sentry user context
 *
 * @SADtrace SAD-003-1, SAD-003-1.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {AppDispatch} dispatch - The dispatch function from the Redux store
 * @param {QueryClient} queryClient - The query client from the React Query library
 *
 * @returns {Promise<void>} A promise that resolves when the user is signed out
 */
export async function signOutUser(
  dispatch: AppDispatch,
  queryClient: QueryClient,
) {
  try {
    // Clear the server-side session record first, while the token is still valid (DLA-1469).
    // Skipped silently on failure: a stale row is replaced by the next login, so this must never
    // be the reason a user cannot sign out.
    try {
      await AuthService.logout();
    } catch (e) {
      captureSentryException(e);
    }

    await cognitoSignOut();
  } catch (e) {
    // Cognito sign-out can fail (network down / already-expired session). Report it to
    // Sentry, but clear the local session regardless so the user is never left
    // logged-in locally with a live token cookie. Don't re-throw — callers await this
    // inside their own catch blocks.
    captureSentryException(e);
  } finally {
    dispatch(authActions.reset());
    cookies.remove(JWT_ACCESS_TOKEN, COOKIE_OPTIONS);
    queryClient.invalidateQueries();

    // Clear Sentry user context
    clearSentryUser();
  }
}
